import ArrowRightRoundedIcon from '@material-ui/icons/ArrowRightRounded'
import DoneRoundedIcon from '@material-ui/icons/DoneRounded'
import classnames from 'classnames'
import * as mixpanel from 'mixpanel-browser'
import * as React from 'react'
import Joyride, { EVENTS, STATUS } from 'react-joyride'

import { RouteComponentProps, withRouter } from 'react-router-dom'
import MainMenuService from '../../services/mainMenu'
import TourService from '../../services/tour'

import './style.scss'

const renderTourIndicators = stepIndex => (
  <div className="tour-indicators-wrap">
    {Array(4)
      .fill(null)
      .map((_, idx) => (
        <span
          className={classnames('indicator', [stepIndex === idx && 'active'])}
          key={idx}
        />
      ))}
  </div>
)

const renderNextBtn = () => (
  <span className="tour-btn-content">
    next <ArrowRightRoundedIcon classes={{ root: 'icon' }} />
  </span>
)

const renderDoneBtn = () => (
  <span className="tour-btn-content">
    done <DoneRoundedIcon classes={{ root: 'icon' }} />
  </span>
)

const getSteps = stepIndex => [
  {
    target: '#questionnaires-tour-step',
    title: 'Questionnaires Tab',
    content: (
      <div className="tour-text">
        View which questionnaires have yet to be completed. The number in the
        red notification dot shows the overall number of questionnaires left to
        complete. {renderTourIndicators(stepIndex)}
      </div>
    ),
    disableBeacon: true,
    locale: {
      next: renderNextBtn()
    }
  },
  {
    target: '#evaluation-games-tour-step',
    title: 'Games Tab',
    content: (
      <div className="tour-text">
        View which game categories have yet to be completed. The number in the
        red notification dot shows the overall number of games left to beat in
        the evaluation game app. {renderTourIndicators(stepIndex)}
      </div>
    ),
    disableScrolling: true,
    locale: {
      next: renderNextBtn()
    }
  },
  // {
  //   target: '#start-game-tour-step',
  //   title: 'Start Games',
  //   content: (
  //     <div className="tour-text">
  //       Opens the evaluation game. If you didn’t install the game app yet, click
  //       here to install it on an iOS or Android device of your choice.
  //       {renderTourIndicators(stepIndex)}
  //     </div>
  //   ),
  //   disableScrolling: true,
  //   locale: {
  //     next: renderNextBtn()
  //   }
  // },
  {
    target: '.parental-instr-tour-step',
    title: 'Parental Instructions',
    content: (
      <div className="tour-text">
        Before playing the games with your child, carefully go over the parental
        instructions, available through the options menu.
        {renderTourIndicators(stepIndex)}
      </div>
    ),
    locale: {
      next: renderNextBtn()
    }
  },
  {
    target: '#abilities-tour-step',
    title: 'Abilities Tab',
    content: (
      <div className="tour-text">
        View your child’s evaluation results. New scores you unlock by
        completing questionnaires and games will be added to the abilities map.
        {renderTourIndicators(stepIndex)}
      </div>
    ),
    locale: {
      last: renderDoneBtn()
    }
  }
]

const Tour: React.FunctionComponent<{
  userId: string
} & RouteComponentProps> = props => {
  const [run, setRun] = React.useState<boolean>(false)
  const [stepIndex, setStepIndex] = React.useState<number>(0)

  const timerScrollToTop = React.useRef<any>()
  const timerAfterOpenMenu = React.useRef<any>()

  const handleStartTourClick = () => {
    window.scrollTo({ top: 0, behavior: 'auto' })

    timerScrollToTop.current = setTimeout(() => {
      setRun(true)
    }, 200)
  }

  React.useEffect(() => {
    TourService.startTour = handleStartTourClick
  }, [])

  React.useEffect(() => {
    const { search } = props.location
    const params = new URLSearchParams(search)

    const tourStepFromSearch: number = Number(params.get('tour_step') as string)

    if (tourStepFromSearch >= 1 && tourStepFromSearch <= 4) {
      if (tourStepFromSearch === 3) {
        MainMenuService.openMenu()

        setStepIndex(tourStepFromSearch - 1)

        handleStartTourClick()
      } else {
        setStepIndex(tourStepFromSearch - 1)

        handleStartTourClick()
      }
    }
  }, [props.location])

  React.useEffect(
    () => () => {
      clearTimeout(timerScrollToTop.current)
      clearTimeout(timerAfterOpenMenu.current)
    },
    []
  )

  const triggerMenuOpen = () => {
    MainMenuService.openMenu()

    timerAfterOpenMenu.current = setTimeout(() => setStepIndex(2), 300)
  }

  const triggerMenuClose = () => {
    MainMenuService.closeMenu()
  }

  const closeTour = type => {
    setRun(false)

    if (type === 'step:after') {
      setStepIndex(0)
      if (props.location.search) {
        props.history.replace({ ...props.location, search: undefined })
      }
    }
  }

  // remove body scroll
  React.useEffect(() => {
    if (run) {
      document.body.className = 'no-scroll'
    } else {
      document.body.classList.remove('no-scroll')
    }

    return () => document.body.classList.remove('no-scroll')
  }, [run])

  const handleJoyrideCallback = data => {
    const { action, status, type } = data

    if (action === 'close') {
      closeTour(type)
    }
    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      if (stepIndex === 0 && action !== 'close') {
        setStepIndex(1)
      }
      if (stepIndex === 1 && action !== 'close') {
        triggerMenuOpen()
      }
      if (stepIndex === 2) {
        triggerMenuClose()
        if (action !== 'close') {
          setStepIndex(3)
        }
      }
      if (stepIndex === 3 && action !== 'close') {
        closeTour(type)
        mixpanel.identify(props.userId)
        mixpanel.track('Took product tour')
      }
    } else if ([STATUS.FINISHED].includes(status)) {
      setRun(false)
    }
  }

  return (
    <Joyride
      styles={{
        options: {
          overlayColor: 'rgba(0, 0, 0, 0.35)',
          textColor: '#413E4D',
          zIndex: 1302
        }
      }}
      disableScrolling={true}
      steps={getSteps(stepIndex)}
      continuous={true}
      hideBackButton={true}
      stepIndex={stepIndex}
      run={run}
      callback={handleJoyrideCallback}
      spotlightPadding={2}
    />
  )
}

export default withRouter(Tour)



// WEBPACK FOOTER //
// ./src/components/Tour/index.tsx